// cspell:disable

import { toIsoAlpha2 } from "./countries";

/**
 *  List of USA state postal codes (same as ISO 3166-2 subdivision code)
 */
export const usaProvinces = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "American Samoa", code: "AS" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "Armed Forces Europe", code: "AE" },
  { name: "Armed Forces Pacific", code: "AP" },
  { name: "Armed Forces America", code: "AA" },
  { name: "Armed Forces Americas", code: "AA" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "District of Columbia", code: "DC" },
  { name: "Federated States of Micronesia", code: "FM" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Guam", code: "GU" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Marshall Islands", code: "MH" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "Northern Mariana Islands", code: "MP" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Palau", code: "PW" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Puerto Rico", code: "PR" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "United States Minor Outlying Islands", code: "UM" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Virgin Islands, U.S.", code: "VI" },
  { name: "Virgin Islands", code: "VI" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" },
] as const;

export const USA_PROVINCE_CODES = usaProvinces.map((province) => province.code);
export const USA_PROVINCE_NAMES = usaProvinces.map((province) => province.name);

export type USAProvinceCode = (typeof usaProvinces)[number]["code"];
export type USAProvinceName = (typeof usaProvinces)[number]["name"];

export const usaProvinceCodes: readonly USAProvinceCode[] = usaProvinces.map(
  (province) => province.code,
);

// 49 contiguous states (all states + DC - Hawaii - Alaska)
export const CONTIGUOUS_USA_PROVINCES: USAProvinceCode[] = [
  "AL",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

export const USA_PROVINCES_BY_CODE = Object.fromEntries(
  usaProvinces.map((province) => [province.code, province.name]),
);
export const USA_PROVINCES_BY_NAME = Object.fromEntries(
  usaProvinces.map((province) => [province.name.toLowerCase(), province.code]),
);

export const findProvinceCode = (province: string) => {
  if (USA_PROVINCES_BY_CODE[province]) {
    return province;
  }
  const code = USA_PROVINCES_BY_NAME[province.toLowerCase()];

  if (code === undefined) {
    return province;
  }
  return code;
};

export const unknownProvinceRepresentationToProvinceName = (
  unknownProvinceRepresentation: string,
) => {
  // If the unknown province representation is a valid USA province code, return the province name
  if (USA_PROVINCES_BY_CODE[unknownProvinceRepresentation]) {
    return USA_PROVINCES_BY_CODE[unknownProvinceRepresentation];
  }
  // Otherwise, return the unknown province representation
  return unknownProvinceRepresentation;
};

export function isUsaProvinceCode(
  province: string,
): province is USAProvinceCode {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return USA_PROVINCE_CODES.includes(province as USAProvinceCode);
}

const SHOPIFY_PROVINCE_CODE_MAP = new Map<string, string>();
// Populated lazily on first call to findShopifyProvinceCode

function ensureShopifyProvinceCodeMap() {
  if (SHOPIFY_PROVINCE_CODE_MAP.size > 0) {
    return;
  }
  for (const entry of shopifyProvinces) {
    SHOPIFY_PROVINCE_CODE_MAP.set(
      `${entry.countryCode}/${entry.province.toLowerCase()}`,
      entry.provinceCode,
    );
  }
}

export function findShopifyProvinceCode(
  countryCode: string,
  province: string,
): string | undefined {
  ensureShopifyProvinceCodeMap();
  return SHOPIFY_PROVINCE_CODE_MAP.get(
    `${countryCode}/${province.toLowerCase()}`,
  );
}

/**
 * List of Canadian province/territory postal codes (same as ISO 3166-2 subdivision code)
 */
export const canadaProvinces = [
  { name: "Alberta", code: "AB" },
  { name: "British Columbia", code: "BC" },
  { name: "Manitoba", code: "MB" },
  { name: "New Brunswick", code: "NB" },
  { name: "Newfoundland and Labrador", code: "NL" },
  { name: "Northwest Territories", code: "NT" },
  { name: "Nova Scotia", code: "NS" },
  { name: "Nunavut", code: "NU" },
  { name: "Ontario", code: "ON" },
  { name: "Prince Edward Island", code: "PE" },
  { name: "Quebec", code: "QC" },
  { name: "Saskatchewan", code: "SK" },
  { name: "Yukon", code: "YT" },
] as const;

export const CANADA_PROVINCE_CODES = canadaProvinces.map(
  (province) => province.code,
);
export const CANADA_PROVINCE_NAMES = canadaProvinces.map(
  (province) => province.name,
);

export type CanadaProvinceCode = (typeof canadaProvinces)[number]["code"];
export type CanadaProvinceName = (typeof canadaProvinces)[number]["name"];

export const CANADA_PROVINCES_BY_CODE = Object.fromEntries(
  canadaProvinces.map((province) => [province.code, province.name]),
);
/** Other names shoppers type, accepted by name lookups but never listed as provinces. */
const canadaProvinceAliases = [
  { name: "Newfoundland", code: "NL" },
  { name: "Québec", code: "QC" },
  { name: "Yukon Territory", code: "YT" },
] as const;

export const CANADA_PROVINCES_BY_NAME = Object.fromEntries(
  [...canadaProvinces, ...canadaProvinceAliases].map((province) => [
    province.name.toLowerCase(),
    province.code,
  ]),
);

export function isCanadaProvinceCode(
  province: string,
): province is CanadaProvinceCode {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return CANADA_PROVINCE_CODES.includes(province as CanadaProvinceCode);
}

export const findCanadaProvinceCode = (province: string): string => {
  if (CANADA_PROVINCES_BY_CODE[province]) {
    return province;
  }
  const code = CANADA_PROVINCES_BY_NAME[province.toLowerCase()];
  if (code === undefined) {
    return province;
  }
  return code;
};

/** Countries whose codes are known ISO 3166-2 suffixes; MX is left out (Shopify "AGS" is ISO "MX-AGU"). */
const ISO_SUBDIVISION_SOURCES: Record<
  string,
  () => readonly { name: string; code: string }[]
> = {
  US: () => usaProvinces,
  CA: () => [...canadaProvinces, ...canadaProvinceAliases],
  AU: () => shopifyProvincesFor("AU"),
  BR: () => shopifyProvincesFor("BR"),
};

function shopifyProvincesFor(countryCode: string) {
  return shopifyProvinces
    .filter((entry) => entry.countryCode === countryCode)
    .map((entry) => ({ name: entry.province, code: entry.provinceCode }));
}

/** ISO 3166-2 code ("US-NY") from a state code, name, or prefixed code; undefined when unsure, since carriers
 * that validate the field reject a wrong code outright. */
export function toIsoSubdivisionCode(
  country: string | null | undefined,
  state: string | null | undefined,
): string | undefined {
  const countryCode = toIsoAlpha2(country);
  const raw = state?.trim();
  const source = countryCode ? ISO_SUBDIVISION_SOURCES[countryCode] : undefined;
  if (!countryCode || !raw || !source) {
    return undefined;
  }
  const prefix = `${countryCode}-`;
  const upper = raw.toUpperCase();
  const code = upper.startsWith(prefix) ? upper.slice(prefix.length) : upper;
  const match = source().find(
    (entry) =>
      entry.code.toUpperCase() === code ||
      entry.name.toLowerCase() === raw.toLowerCase(),
  );
  return match ? `${prefix}${match.code.toUpperCase()}` : undefined;
}

/**
 * Provinces that Shopify recognizes. A good starting place for provinces in the world.
 * source: https://matrixify.app/documentation/shopify-address-countries-and-provinces/
 */
export const shopifyProvinces: {
  country: string;
  countryCode: string;
  province: string;
  provinceCode: string;
}[] = [
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Buenos Aires",
    provinceCode: "B",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Catamarca",
    provinceCode: "K",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Chaco",
    provinceCode: "H",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Chubut",
    provinceCode: "U",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Ciudad Autónoma de Buenos Aires",
    provinceCode: "C",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Córdoba",
    provinceCode: "X",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Corrientes",
    provinceCode: "W",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Entre Ríos",
    provinceCode: "E",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Formosa",
    provinceCode: "P",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Jujuy",
    provinceCode: "Y",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "La Pampa",
    provinceCode: "L",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "La Rioja",
    provinceCode: "F",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Mendoza",
    provinceCode: "M",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Misiones",
    provinceCode: "N",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Neuquén",
    provinceCode: "Q",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Río Negro",
    provinceCode: "R",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Salta",
    provinceCode: "A",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "San Juan",
    provinceCode: "J",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "San Luis",
    provinceCode: "D",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Santa Cruz",
    provinceCode: "Z",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Santa Fe",
    provinceCode: "S",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Santiago Del Estero",
    provinceCode: "G",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Tierra Del Fuego",
    provinceCode: "V",
  },
  {
    country: "Argentina",
    countryCode: "AR",
    province: "Tucumán",
    provinceCode: "T",
  },
  {
    country: "Australia",
    countryCode: "AU",
    province: "Australian Capital Territory",
    provinceCode: "ACT",
  },
  {
    country: "Australia",
    countryCode: "AU",
    province: "New South Wales",
    provinceCode: "NSW",
  },
  {
    country: "Australia",
    countryCode: "AU",
    province: "Northern Territory",
    provinceCode: "NT",
  },
  {
    country: "Australia",
    countryCode: "AU",
    province: "Queensland",
    provinceCode: "QLD",
  },
  {
    country: "Australia",
    countryCode: "AU",
    province: "South Australia",
    provinceCode: "SA",
  },
  {
    country: "Australia",
    countryCode: "AU",
    province: "Tasmania",
    provinceCode: "TAS",
  },
  {
    country: "Australia",
    countryCode: "AU",
    province: "Victoria",
    provinceCode: "VIC",
  },
  {
    country: "Australia",
    countryCode: "AU",
    province: "Western Australia",
    provinceCode: "WA",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Acre",
    provinceCode: "AC",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Alagoas",
    provinceCode: "AL",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Amapá",
    provinceCode: "AP",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Amazonas",
    provinceCode: "AM",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Bahia",
    provinceCode: "BA",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Ceará",
    provinceCode: "CE",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Distrito Federal",
    provinceCode: "DF",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Espírito Santo",
    provinceCode: "ES",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Goiás",
    provinceCode: "GO",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Maranhão",
    provinceCode: "MA",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Mato Grosso",
    provinceCode: "MT",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Mato Grosso do Sul",
    provinceCode: "MS",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Minas Gerais",
    provinceCode: "MG",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Pará",
    provinceCode: "PA",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Paraíba",
    provinceCode: "PB",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Paraná",
    provinceCode: "PR",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Pernambuco",
    provinceCode: "PE",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Piauí",
    provinceCode: "PI",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Rio de Janeiro",
    provinceCode: "RJ",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Rio Grande do Norte",
    provinceCode: "RN",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Rio Grande do Sul",
    provinceCode: "RS",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Rondônia",
    provinceCode: "RO",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Roraima",
    provinceCode: "RR",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Santa Catarina",
    provinceCode: "SC",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "São Paulo",
    provinceCode: "SP",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Sergipe",
    provinceCode: "SE",
  },
  {
    country: "Brazil",
    countryCode: "BR",
    province: "Tocantins",
    provinceCode: "TO",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Alberta",
    provinceCode: "AB",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "British Columbia",
    provinceCode: "BC",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Manitoba",
    provinceCode: "MB",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "New Brunswick",
    provinceCode: "NB",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Newfoundland and Labrador",
    provinceCode: "NL",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Northwest Territories",
    provinceCode: "NT",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Nova Scotia",
    provinceCode: "NS",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Nunavut",
    provinceCode: "NU",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Ontario",
    provinceCode: "ON",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Prince Edward Island",
    provinceCode: "PE",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Quebec",
    provinceCode: "QC",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Saskatchewan",
    provinceCode: "SK",
  },
  {
    country: "Canada",
    countryCode: "CA",
    province: "Yukon",
    provinceCode: "YT",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Arica and Parinacota",
    provinceCode: "AP",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Tarapacá",
    provinceCode: "TA",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Antofagasta",
    provinceCode: "AN",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Atacama",
    provinceCode: "AT",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Coquimbo",
    provinceCode: "CO",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Valparaíso",
    provinceCode: "VS",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Santiago",
    provinceCode: "RM",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "O’Higgins",
    provinceCode: "LI",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Maule",
    provinceCode: "ML",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Ñuble",
    provinceCode: "NB",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Biobío",
    provinceCode: "BI",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Araucanía",
    provinceCode: "AR",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Los Ríos",
    provinceCode: "LR",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Los Lagos",
    provinceCode: "LL",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Aysén",
    provinceCode: "AI",
  },
  {
    country: "Chile",
    countryCode: "CL",
    province: "Magallanes",
    provinceCode: "MA",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Anhui",
    provinceCode: "AH",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Beijing",
    provinceCode: "BJ",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Chongqing",
    provinceCode: "CQ",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Fujian",
    provinceCode: "FJ",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Gansu",
    provinceCode: "GS",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Guangdong",
    provinceCode: "GD",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Guangxi",
    provinceCode: "GX",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Guizhou",
    provinceCode: "GZ",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Hainan",
    provinceCode: "HI",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Hebei",
    provinceCode: "HE",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Heilongjiang",
    provinceCode: "HL",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Henan",
    provinceCode: "HA",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Hubei",
    provinceCode: "HB",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Hunan",
    provinceCode: "HN",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Inner Mongolia",
    provinceCode: "NM",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Jiangsu",
    provinceCode: "JS",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Jiangxi",
    provinceCode: "JX",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Jilin",
    provinceCode: "JL",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Liaoning",
    provinceCode: "LN",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Ningxia",
    provinceCode: "NX",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Qinghai",
    provinceCode: "QH",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Shaanxi",
    provinceCode: "SN",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Shandong",
    provinceCode: "SD",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Shanghai",
    provinceCode: "SH",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Shanxi",
    provinceCode: "SX",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Sichuan",
    provinceCode: "SC",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Tianjin",
    provinceCode: "TJ",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Xinjiang",
    provinceCode: "XJ",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Xizang",
    provinceCode: "YZ",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Yunnan",
    provinceCode: "YN",
  },
  {
    country: "China",
    countryCode: "CN",
    province: "Zhejiang",
    provinceCode: "ZJ",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Amazonas",
    provinceCode: "AMA",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Antioquia",
    provinceCode: "ANT",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Arauca",
    provinceCode: "ARA",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Atlántico",
    provinceCode: "ATL",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Bogotá, D.C.",
    provinceCode: "DC",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Bolívar",
    provinceCode: "BOL",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Boyacá",
    provinceCode: "BOY",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Caldas",
    provinceCode: "CAL",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Caquetá",
    provinceCode: "CAQ",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Casanare",
    provinceCode: "CAS",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Cauca",
    provinceCode: "CAU",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Cesar",
    provinceCode: "CES",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Chocó",
    provinceCode: "CHO",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Córdoba",
    provinceCode: "COR",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Cundinamarca",
    provinceCode: "CUN",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Guainía",
    provinceCode: "GUA",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Guaviare",
    provinceCode: "GUV",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Huila",
    provinceCode: "HUI",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "La Guajira",
    provinceCode: "LAG",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Magdalena",
    provinceCode: "MAG",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Meta",
    provinceCode: "MET",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Nariño",
    provinceCode: "NAR",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Norte de Santander",
    provinceCode: "NSA",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Putumayo",
    provinceCode: "PUT",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Quindío",
    provinceCode: "QUI",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Risaralda",
    provinceCode: "RIS",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "San Andrés, Providencia y Santa Catalina",
    provinceCode: "SAP",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Santander",
    provinceCode: "SAN",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Sucre",
    provinceCode: "SUC",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Tolima",
    provinceCode: "TOL",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Valle del Cauca",
    provinceCode: "VAC",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Vaupés",
    provinceCode: "VAU",
  },
  {
    country: "Colombia",
    countryCode: "CO",
    province: "Vichada",
    provinceCode: "VID",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "6th of October",
    provinceCode: "SU",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Al Sharqia",
    provinceCode: "SHR",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Alexandria",
    provinceCode: "ALX",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Aswan",
    provinceCode: "ASN",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Asyut",
    provinceCode: "AST",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Beheira",
    provinceCode: "BH",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Beni Suef",
    provinceCode: "BNS",
  },
  { country: "Egypt", countryCode: "EG", province: "Cairo", provinceCode: "C" },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Dakahlia",
    provinceCode: "DK",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Damietta",
    provinceCode: "DT",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Faiyum",
    provinceCode: "FYM",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Gharbia",
    provinceCode: "GH",
  },
  { country: "Egypt", countryCode: "EG", province: "Giza", provinceCode: "GZ" },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Helwan",
    provinceCode: "HU",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Ismailia",
    provinceCode: "IS",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Kafr el-Sheikh",
    provinceCode: "KFS",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Luxor",
    provinceCode: "LX",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Matrouh",
    provinceCode: "MT",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Minya",
    provinceCode: "MN",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Monufia",
    provinceCode: "MNF",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "New Valley",
    provinceCode: "WAD",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "North Sinai",
    provinceCode: "SIN",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Port Said",
    provinceCode: "PTS",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Qalyubia",
    provinceCode: "KB",
  },
  { country: "Egypt", countryCode: "EG", province: "Qena", provinceCode: "KN" },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Red Sea",
    provinceCode: "BA",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Sohag",
    provinceCode: "SHG",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "South Sinai",
    provinceCode: "JS",
  },
  {
    country: "Egypt",
    countryCode: "EG",
    province: "Suez",
    provinceCode: "SUZ",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Alta Verapaz",
    provinceCode: "AVE",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Baja Verapaz",
    provinceCode: "BVE",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Chimaltenango",
    provinceCode: "CMT",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Chiquimula",
    provinceCode: "CQM",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "El Progreso",
    provinceCode: "EPR",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Escuintla",
    provinceCode: "ESC",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Guatemala",
    provinceCode: "GUA",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Huehuetenango",
    provinceCode: "HUE",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Izabal",
    provinceCode: "IZA",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Jalapa",
    provinceCode: "JAL",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Jutiapa",
    provinceCode: "JUT",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Petén",
    provinceCode: "PET",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Quetzaltenango",
    provinceCode: "QUE",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Quiché",
    provinceCode: "QUI",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Retalhuleu",
    provinceCode: "RET",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Sacatepéquez",
    provinceCode: "SAC",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "San Marcos",
    provinceCode: "SMA",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Santa Rosa",
    provinceCode: "SRO",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Sololá",
    provinceCode: "SOL",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Suchitepéquez",
    provinceCode: "SUC",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Totonicapán",
    provinceCode: "TOT",
  },
  {
    country: "Guatemala",
    countryCode: "GT",
    province: "Zacapa",
    provinceCode: "ZAC",
  },
  {
    country: "Hong Kong",
    countryCode: "HK",
    province: "Hong Kong Island",
    provinceCode: "HK",
  },
  {
    country: "Hong Kong",
    countryCode: "HK",
    province: "Kowloon",
    provinceCode: "KL",
  },
  {
    country: "Hong Kong",
    countryCode: "HK",
    province: "New Territories",
    provinceCode: "NT",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Andaman and Nicobar Islands",
    provinceCode: "AN",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Andhra Pradesh",
    provinceCode: "AP",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Arunachal Pradesh",
    provinceCode: "AR",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Assam",
    provinceCode: "AS",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Bihar",
    provinceCode: "BR",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Chandigarh",
    provinceCode: "CH",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Chhattisgarh",
    provinceCode: "CG",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Dadra and Nagar Haveli",
    provinceCode: "DN",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Daman and Diu",
    provinceCode: "DD",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Delhi",
    provinceCode: "DL",
  },
  { country: "India", countryCode: "IN", province: "Goa", provinceCode: "GA" },
  {
    country: "India",
    countryCode: "IN",
    province: "Gujarat",
    provinceCode: "GJ",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Haryana",
    provinceCode: "HR",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Himachal Pradesh",
    provinceCode: "HP",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Jammu and Kashmir",
    provinceCode: "JK",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Jharkhand",
    provinceCode: "JH",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Karnataka",
    provinceCode: "KA",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Kerala",
    provinceCode: "KL",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Ladakh",
    provinceCode: "LA",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Lakshadweep",
    provinceCode: "LD",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Madhya Pradesh",
    provinceCode: "MP",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Maharashtra",
    provinceCode: "MH",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Manipur",
    provinceCode: "MN",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Meghalaya",
    provinceCode: "ML",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Mizoram",
    provinceCode: "MZ",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Nagaland",
    provinceCode: "NL",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Odisha",
    provinceCode: "OR",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Puducherry",
    provinceCode: "PY",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Punjab",
    provinceCode: "PB",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Rajasthan",
    provinceCode: "RJ",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Sikkim",
    provinceCode: "SK",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Tamil Nadu",
    provinceCode: "TN",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Telangana",
    provinceCode: "TS",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Tripura",
    provinceCode: "TR",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Uttar Pradesh",
    provinceCode: "UP",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "Uttarakhand",
    provinceCode: "UK",
  },
  {
    country: "India",
    countryCode: "IN",
    province: "West Bengal",
    provinceCode: "WB",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Aceh",
    provinceCode: "AC",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Bali",
    provinceCode: "BA",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Bangka Belitung",
    provinceCode: "BB",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Banten",
    provinceCode: "BT",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Bengkulu",
    provinceCode: "BE",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Gorontalo",
    provinceCode: "GO",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Jakarta",
    provinceCode: "JK",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Jambi",
    provinceCode: "JA",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Jawa Barat",
    provinceCode: "JB",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Jawa Tengah",
    provinceCode: "JT",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Jawa Timur",
    provinceCode: "JI",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Kalimantan Barat",
    provinceCode: "KB",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Kalimantan Selatan",
    provinceCode: "KS",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Kalimantan Tengah",
    provinceCode: "KT",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Kalimantan Timur",
    provinceCode: "KI",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Kalimantan Utara",
    provinceCode: "KU",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Kepulauan Riau",
    provinceCode: "KR",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Lampung",
    provinceCode: "LA",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Maluku",
    provinceCode: "MA",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Maluku Utara",
    provinceCode: "MU",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "North Sumatra",
    provinceCode: "SU",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Nusa Tenggara Barat",
    provinceCode: "NB",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Nusa Tenggara Timur",
    provinceCode: "NT",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Papua",
    provinceCode: "PA",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Papua Barat",
    provinceCode: "PB",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Riau",
    provinceCode: "RI",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "South Sumatra",
    provinceCode: "SS",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Sulawesi Barat",
    provinceCode: "SR",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Sulawesi Selatan",
    provinceCode: "SN",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Sulawesi Tengah",
    provinceCode: "ST",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Sulawesi Tenggara",
    provinceCode: "SG",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Sulawesi Utara",
    provinceCode: "SA",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "West Sumatra",
    provinceCode: "SB",
  },
  {
    country: "Indonesia",
    countryCode: "ID",
    province: "Yogyakarta",
    provinceCode: "YO",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Carlow",
    provinceCode: "CW",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Cavan",
    provinceCode: "CN",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Clare",
    provinceCode: "CE",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Cork",
    provinceCode: "CO",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Donegal",
    provinceCode: "DL",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Dublin",
    provinceCode: "D",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Galway",
    provinceCode: "G",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Kerry",
    provinceCode: "KY",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Kildare",
    provinceCode: "KE",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Kilkenny",
    provinceCode: "KK",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Laois",
    provinceCode: "LS",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Leitrim",
    provinceCode: "LM",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Limerick",
    provinceCode: "LK",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Longford",
    provinceCode: "LD",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Louth",
    provinceCode: "LH",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Mayo",
    provinceCode: "MO",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Meath",
    provinceCode: "MH",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Monaghan",
    provinceCode: "MN",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Offaly",
    provinceCode: "OY",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Roscommon",
    provinceCode: "RN",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Sligo",
    provinceCode: "SO",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Tipperary",
    provinceCode: "TA",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Waterford",
    provinceCode: "WD",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Westmeath",
    provinceCode: "WH",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Wexford",
    provinceCode: "WX",
  },
  {
    country: "Ireland",
    countryCode: "IE",
    province: "Wicklow",
    provinceCode: "WW",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Agrigento",
    provinceCode: "AG",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Alessandria",
    provinceCode: "AL",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Ancona",
    provinceCode: "AN",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Aosta",
    provinceCode: "AO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Arezzo",
    provinceCode: "AR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Ascoli Piceno",
    provinceCode: "AP",
  },
  { country: "Italy", countryCode: "IT", province: "Asti", provinceCode: "AT" },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Avellino",
    provinceCode: "AV",
  },
  { country: "Italy", countryCode: "IT", province: "Bari", provinceCode: "BA" },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Barletta-Andria-Trani",
    provinceCode: "BT",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Belluno",
    provinceCode: "BL",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Benevento",
    provinceCode: "BN",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Bergamo",
    provinceCode: "BG",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Biella",
    provinceCode: "BI",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Bologna",
    provinceCode: "BO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Bolzano",
    provinceCode: "BZ",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Brescia",
    provinceCode: "BS",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Brindisi",
    provinceCode: "BR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Cagliari",
    provinceCode: "CA",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Caltanissetta",
    provinceCode: "CL",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Campobasso",
    provinceCode: "CB",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Carbonia-Iglesias",
    provinceCode: "CI",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Caserta",
    provinceCode: "CE",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Catania",
    provinceCode: "CT",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Catanzaro",
    provinceCode: "CZ",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Chieti",
    provinceCode: "CH",
  },
  { country: "Italy", countryCode: "IT", province: "Como", provinceCode: "CO" },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Cosenza",
    provinceCode: "CS",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Cremona",
    provinceCode: "CR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Crotone",
    provinceCode: "KR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Cuneo",
    provinceCode: "CN",
  },
  { country: "Italy", countryCode: "IT", province: "Enna", provinceCode: "EN" },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Fermo",
    provinceCode: "FM",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Ferrara",
    provinceCode: "FE",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Firenze",
    provinceCode: "FI",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Foggia",
    provinceCode: "FG",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Forlì-Cesena",
    provinceCode: "FC",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Frosinone",
    provinceCode: "FR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Genova",
    provinceCode: "GE",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Gorizia",
    provinceCode: "GO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Grosseto",
    provinceCode: "GR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Imperia",
    provinceCode: "IM",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Isernia",
    provinceCode: "IS",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "L’Aquila",
    provinceCode: "AQ",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "La Spezia",
    provinceCode: "SP",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Latina",
    provinceCode: "LT",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Lecce",
    provinceCode: "LE",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Lecco",
    provinceCode: "LC",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Livorno",
    provinceCode: "LI",
  },
  { country: "Italy", countryCode: "IT", province: "Lodi", provinceCode: "LO" },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Lucca",
    provinceCode: "LU",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Macerata",
    provinceCode: "MC",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Mantova",
    provinceCode: "MN",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Massa-Carrara",
    provinceCode: "MS",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Matera",
    provinceCode: "MT",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Medio Campidano",
    provinceCode: "VS",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Messina",
    provinceCode: "ME",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Milano",
    provinceCode: "MI",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Modena",
    provinceCode: "MO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Monza e Brianza",
    provinceCode: "MB",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Napoli",
    provinceCode: "NA",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Novara",
    provinceCode: "NO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Nuoro",
    provinceCode: "NU",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Ogliastra",
    provinceCode: "OG",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Olbia-Tempio",
    provinceCode: "OT",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Oristano",
    provinceCode: "OR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Padova",
    provinceCode: "PD",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Palermo",
    provinceCode: "PA",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Parma",
    provinceCode: "PR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Pavia",
    provinceCode: "PV",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Perugia",
    provinceCode: "PG",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Pesaro e Urbino",
    provinceCode: "PU",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Pescara",
    provinceCode: "PE",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Piacenza",
    provinceCode: "PC",
  },
  { country: "Italy", countryCode: "IT", province: "Pisa", provinceCode: "PI" },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Pistoia",
    provinceCode: "PT",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Pordenone",
    provinceCode: "PN",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Potenza",
    provinceCode: "PZ",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Prato",
    provinceCode: "PO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Ragusa",
    provinceCode: "RG",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Ravenna",
    provinceCode: "RA",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Reggio Calabria",
    provinceCode: "RC",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Reggio Emilia",
    provinceCode: "RE",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Rieti",
    provinceCode: "RI",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Rimini",
    provinceCode: "RN",
  },
  { country: "Italy", countryCode: "IT", province: "Roma", provinceCode: "RM" },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Rovigo",
    provinceCode: "RO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Salerno",
    provinceCode: "SA",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Sassari",
    provinceCode: "SS",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Savona",
    provinceCode: "SV",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Siena",
    provinceCode: "SI",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Siracusa",
    provinceCode: "SR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Sondrio",
    provinceCode: "SO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Taranto",
    provinceCode: "TA",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Teramo",
    provinceCode: "TE",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Terni",
    provinceCode: "TR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Torino",
    provinceCode: "TO",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Trapani",
    provinceCode: "TP",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Trento",
    provinceCode: "TN",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Treviso",
    provinceCode: "TV",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Trieste",
    provinceCode: "TS",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Udine",
    provinceCode: "UD",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Varese",
    provinceCode: "VA",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Venezia",
    provinceCode: "VE",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Verbano-Cusio-Ossola",
    provinceCode: "VB",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Vercelli",
    provinceCode: "VC",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Verona",
    provinceCode: "VR",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Vibo Valentia",
    provinceCode: "VV",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Vicenza",
    provinceCode: "VI",
  },
  {
    country: "Italy",
    countryCode: "IT",
    province: "Viterbo",
    provinceCode: "VT",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Hokkaidō",
    provinceCode: "JP-01",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Aomori",
    provinceCode: "JP-02",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Iwate",
    provinceCode: "JP-03",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Miyagi",
    provinceCode: "JP-04",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Akita",
    provinceCode: "JP-05",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Yamagata",
    provinceCode: "JP-06",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Fukushima",
    provinceCode: "JP-07",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Ibaraki",
    provinceCode: "JP-08",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Tochigi",
    provinceCode: "JP-09",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Gunma",
    provinceCode: "JP-10",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Saitama",
    provinceCode: "JP-11",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Chiba",
    provinceCode: "JP-12",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Tōkyō",
    provinceCode: "JP-13",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Kanagawa",
    provinceCode: "JP-14",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Niigata",
    provinceCode: "JP-15",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Toyama",
    provinceCode: "JP-16",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Ishikawa",
    provinceCode: "JP-17",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Fukui",
    provinceCode: "JP-18",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Yamanashi",
    provinceCode: "JP-19",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Nagano",
    provinceCode: "JP-20",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Gifu",
    provinceCode: "JP-21",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Shizuoka",
    provinceCode: "JP-22",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Aichi",
    provinceCode: "JP-23",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Mie",
    provinceCode: "JP-24",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Shiga",
    provinceCode: "JP-25",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Kyōto",
    provinceCode: "JP-26",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Ōsaka",
    provinceCode: "JP-27",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Hyōgo",
    provinceCode: "JP-28",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Nara",
    provinceCode: "JP-29",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Wakayama",
    provinceCode: "JP-30",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Tottori",
    provinceCode: "JP-31",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Shimane",
    provinceCode: "JP-32",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Okayama",
    provinceCode: "JP-33",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Hiroshima",
    provinceCode: "JP-34",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Yamaguchi",
    provinceCode: "JP-35",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Tokushima",
    provinceCode: "JP-36",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Kagawa",
    provinceCode: "JP-37",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Ehime",
    provinceCode: "JP-38",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Kōchi",
    provinceCode: "JP-39",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Fukuoka",
    provinceCode: "JP-40",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Saga",
    provinceCode: "JP-41",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Nagasaki",
    provinceCode: "JP-42",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Kumamoto",
    provinceCode: "JP-43",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Ōita",
    provinceCode: "JP-44",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Miyazaki",
    provinceCode: "JP-45",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Kagoshima",
    provinceCode: "JP-46",
  },
  {
    country: "Japan",
    countryCode: "JP",
    province: "Okinawa",
    provinceCode: "JP-47",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Johor",
    provinceCode: "JHR",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Kedah",
    provinceCode: "KDH",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Kelantan",
    provinceCode: "KTN",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Kuala Lumpur",
    provinceCode: "KUL",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Labuan",
    provinceCode: "LBN",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Melaka",
    provinceCode: "MLK",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Negeri Sembilan",
    provinceCode: "NSN",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Pahang",
    provinceCode: "PHG",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Penang",
    provinceCode: "PNG",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Perak",
    provinceCode: "PRK",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Perlis",
    provinceCode: "PLS",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Putrajaya",
    provinceCode: "PJY",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Sabah",
    provinceCode: "SBH",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Sarawak",
    provinceCode: "SWK",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Selangor",
    provinceCode: "SGR",
  },
  {
    country: "Malaysia",
    countryCode: "MY",
    province: "Terengganu",
    provinceCode: "TRG",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Aguascalientes",
    provinceCode: "AGS",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Baja California",
    provinceCode: "BC",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Baja California Sur",
    provinceCode: "BCS",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Campeche",
    provinceCode: "CAMP",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Chiapas",
    provinceCode: "CHIS",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Chihuahua",
    provinceCode: "CHIH",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Ciudad de México",
    provinceCode: "DF",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Coahuila",
    provinceCode: "COAH",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Colima",
    provinceCode: "COL",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Durango",
    provinceCode: "DGO",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Guanajuato",
    provinceCode: "GTO",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Guerrero",
    provinceCode: "GRO",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Hidalgo",
    provinceCode: "HGO",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Jalisco",
    provinceCode: "JAL",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "México",
    provinceCode: "MEX",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Michoacán",
    provinceCode: "MICH",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Morelos",
    provinceCode: "MOR",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Nayarit",
    provinceCode: "NAY",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Nuevo León",
    provinceCode: "NL",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Oaxaca",
    provinceCode: "OAX",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Puebla",
    provinceCode: "PUE",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Querétaro",
    provinceCode: "QRO",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Quintana Roo",
    provinceCode: "Q ROO",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "San Luis Potosí",
    provinceCode: "SLP",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Sinaloa",
    provinceCode: "SIN",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Sonora",
    provinceCode: "SON",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Tabasco",
    provinceCode: "TAB",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Tamaulipas",
    provinceCode: "TAMPS",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Tlaxcala",
    provinceCode: "TLAX",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Veracruz",
    provinceCode: "VER",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Yucatán",
    provinceCode: "YUC",
  },
  {
    country: "Mexico",
    countryCode: "MX",
    province: "Zacatecas",
    provinceCode: "ZAC",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Auckland",
    provinceCode: "AUK",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Bay of Plenty",
    provinceCode: "BOP",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Canterbury",
    provinceCode: "CAN",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Chatham Islands",
    provinceCode: "CIT",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Gisborne",
    provinceCode: "GIS",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Hawke’s Bay",
    provinceCode: "HKB",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Manawatu-Wanganui",
    provinceCode: "MWT",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Marlborough",
    provinceCode: "MBH",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Nelson",
    provinceCode: "NSN",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Northland",
    provinceCode: "NTL",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Otago",
    provinceCode: "OTA",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Southland",
    provinceCode: "STL",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Taranaki",
    provinceCode: "TKI",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Tasman",
    provinceCode: "TAS",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Waikato",
    provinceCode: "WKO",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "Wellington",
    provinceCode: "WGN",
  },
  {
    country: "New Zealand",
    countryCode: "NZ",
    province: "West Coast",
    provinceCode: "WTC",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Abia",
    provinceCode: "AB",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Abuja Federal Capital Territory",
    provinceCode: "FC",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Adamawa",
    provinceCode: "AD",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Akwa Ibom",
    provinceCode: "AK",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Anambra",
    provinceCode: "AN",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Bauchi",
    provinceCode: "BA",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Bayelsa",
    provinceCode: "BY",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Benue",
    provinceCode: "BE",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Borno",
    provinceCode: "BO",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Cross River",
    provinceCode: "CR",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Delta",
    provinceCode: "DE",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Ebonyi",
    provinceCode: "EB",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Edo",
    provinceCode: "ED",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Ekiti",
    provinceCode: "EK",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Enugu",
    provinceCode: "EN",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Gombe",
    provinceCode: "GO",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Imo",
    provinceCode: "IM",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Jigawa",
    provinceCode: "JI",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Kaduna",
    provinceCode: "KD",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Kano",
    provinceCode: "KN",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Katsina",
    provinceCode: "KT",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Kebbi",
    provinceCode: "KE",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Kogi",
    provinceCode: "KO",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Kwara",
    provinceCode: "KW",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Lagos",
    provinceCode: "LA",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Nasarawa",
    provinceCode: "NA",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Niger",
    provinceCode: "NI",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Ogun",
    provinceCode: "OG",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Ondo",
    provinceCode: "ON",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Osun",
    provinceCode: "OS",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Oyo",
    provinceCode: "OY",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Plateau",
    provinceCode: "PL",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Rivers",
    provinceCode: "RI",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Sokoto",
    provinceCode: "SO",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Taraba",
    provinceCode: "TA",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Yobe",
    provinceCode: "YO",
  },
  {
    country: "Nigeria",
    countryCode: "NG",
    province: "Zamfara",
    provinceCode: "ZA",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Bocas del Toro",
    provinceCode: "PA-1",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Chiriquí",
    provinceCode: "PA-4",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Coclé",
    provinceCode: "PA-2",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Colón",
    provinceCode: "PA-3",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Darién",
    provinceCode: "PA-5",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Emberá",
    provinceCode: "PA-EM",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Herrera",
    provinceCode: "PA-6",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Kuna Yala",
    provinceCode: "PA-KY",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Los Santos",
    provinceCode: "PA-7",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Ngöbe-Buglé",
    provinceCode: "PA-NB",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Panamá",
    provinceCode: "PA-8",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Panamá Oeste",
    provinceCode: "PA-10",
  },
  {
    country: "Panama",
    countryCode: "PA",
    province: "Veraguas",
    provinceCode: "PA-9",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Amazonas",
    provinceCode: "PE-AMA",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Áncash",
    provinceCode: "PE-ANC",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Apurímac",
    provinceCode: "PE-APU",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Arequipa",
    provinceCode: "PE-ARE",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Ayacucho",
    provinceCode: "PE-AYA",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Cajamarca",
    provinceCode: "PE-CAJ",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Callao",
    provinceCode: "PE-CAL",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Cuzco",
    provinceCode: "PE-CUS",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Huancavelica",
    provinceCode: "PE-HUV",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Huánuco",
    provinceCode: "PE-HUC",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Ica",
    provinceCode: "PE-ICA",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Junín",
    provinceCode: "PE-JUN",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "La Libertad",
    provinceCode: "PE-LAL",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Lambayeque",
    provinceCode: "PE-LAM",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Lima (departamento)",
    provinceCode: "PE-LIM",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Lima (provincia)",
    provinceCode: "PE-LMA",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Loreto",
    provinceCode: "PE-LOR",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Madre de Dios",
    provinceCode: "PE-MDD",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Moquegua",
    provinceCode: "PE-MOQ",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Pasco",
    provinceCode: "PE-PAS",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Piura",
    provinceCode: "PE-PIU",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Puno",
    provinceCode: "PE-PUN",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "San Martín",
    provinceCode: "PE-SAM",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Tacna",
    provinceCode: "PE-TAC",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Tumbes",
    provinceCode: "PE-TUM",
  },
  {
    country: "Peru",
    countryCode: "PE",
    province: "Ucayali",
    provinceCode: "PE-UCA",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Abra",
    provinceCode: "PH-ABR",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Agusan del Norte",
    provinceCode: "PH-AGN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Agusan del Sur",
    provinceCode: "PH-AGS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Aklan",
    provinceCode: "PH-AKL",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Albay",
    provinceCode: "PH-ALB",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Antique",
    provinceCode: "PH-ANT",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Apayao",
    provinceCode: "PH-APA",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Aurora",
    provinceCode: "PH-AUR",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Basilan",
    provinceCode: "PH-BAS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Bataan",
    provinceCode: "PH-BAN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Batanes",
    provinceCode: "PH-BTN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Batangas",
    provinceCode: "PH-BTG",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Benguet",
    provinceCode: "PH-BEN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Biliran",
    provinceCode: "PH-BIL",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Bohol",
    provinceCode: "PH-BOH",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Bukidnon",
    provinceCode: "PH-BUK",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Bulacan",
    provinceCode: "PH-BUL",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Cagayan",
    provinceCode: "PH-CAG",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Camarines Norte",
    provinceCode: "PH-CAN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Camarines Sur",
    provinceCode: "PH-CAS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Camiguin",
    provinceCode: "PH-CAM",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Capiz",
    provinceCode: "PH-CAP",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Catanduanes",
    provinceCode: "PH-CAT",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Cavite",
    provinceCode: "PH-CAV",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Cebu",
    provinceCode: "PH-CEB",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Cotabato",
    provinceCode: "PH-NCO",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Davao de Oro",
    provinceCode: "PH-COM",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Davao del Norte",
    provinceCode: "PH-DAV",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Davao del Sur",
    provinceCode: "PH-DAS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Davao Occidental",
    provinceCode: "PH-DVO",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Davao Oriental",
    provinceCode: "PH-DAO",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Dinagat Islands",
    provinceCode: "PH-DIN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Eastern Samar",
    provinceCode: "PH-EAS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Guimaras",
    provinceCode: "PH-GUI",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Ifugao",
    provinceCode: "PH-IFU",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Ilocos Norte",
    provinceCode: "PH-ILN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Ilocos Sur",
    provinceCode: "PH-ILS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Iloilo",
    provinceCode: "PH-ILI",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Isabela",
    provinceCode: "PH-ISA",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Kalinga",
    provinceCode: "PH-KAL",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "La Union",
    provinceCode: "PH-LUN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Laguna",
    provinceCode: "PH-LAG",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Lanao del Norte",
    provinceCode: "PH-LAN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Lanao del Sur",
    provinceCode: "PH-LAS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Leyte",
    provinceCode: "PH-LEY",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Maguindanao",
    provinceCode: "PH-MAG",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Marinduque",
    provinceCode: "PH-MAD",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Masbate",
    provinceCode: "PH-MAS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Metro Manila",
    provinceCode: "PH-00",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Misamis Occidental",
    provinceCode: "PH-MSC",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Misamis Oriental",
    provinceCode: "PH-MSR",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Mountain Province",
    provinceCode: "PH-MOU",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Negros Occidental",
    provinceCode: "PH-NEC",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Negros Oriental",
    provinceCode: "PH-NER",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Northern Samar",
    provinceCode: "PH-NSA",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Nueva Ecija",
    provinceCode: "PH-NUE",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Nueva Vizcaya",
    provinceCode: "PH-NUV",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Occidental Mindoro",
    provinceCode: "PH-MDC",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Oriental Mindoro",
    provinceCode: "PH-MDR",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Palawan",
    provinceCode: "PH-PLW",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Pampanga",
    provinceCode: "PH-PAM",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Pangasinan",
    provinceCode: "PH-PAN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Quezon",
    provinceCode: "PH-QUE",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Quirino",
    provinceCode: "PH-QUI",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Rizal",
    provinceCode: "PH-RIZ",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Romblon",
    provinceCode: "PH-ROM",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Samar",
    provinceCode: "PH-WSA",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Sarangani",
    provinceCode: "PH-SAR",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Siquijor",
    provinceCode: "PH-SIG",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Sorsogon",
    provinceCode: "PH-SOR",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "South Cotabato",
    provinceCode: "PH-SCO",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Southern Leyte",
    provinceCode: "PH-SLE",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Sultan Kudarat",
    provinceCode: "PH-SUK",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Sulu",
    provinceCode: "PH-SLU",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Surigao del Norte",
    provinceCode: "PH-SUN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Surigao del Sur",
    provinceCode: "PH-SUR",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Tarlac",
    provinceCode: "PH-TAR",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Tawi-Tawi",
    provinceCode: "PH-TAW",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Zambales",
    provinceCode: "PH-ZMB",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Zamboanga del Norte",
    provinceCode: "PH-ZAN",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Zamboanga del Sur",
    provinceCode: "PH-ZAS",
  },
  {
    country: "Philippines",
    countryCode: "PH",
    province: "Zamboanga Sibugay",
    provinceCode: "PH-ZSI",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Açores",
    provinceCode: "PT-20",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Aveiro",
    provinceCode: "PT-01",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Beja",
    provinceCode: "PT-02",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Braga",
    provinceCode: "PT-03",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Bragança",
    provinceCode: "PT-04",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Castelo Branco",
    provinceCode: "PT-05",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Coimbra",
    provinceCode: "PT-06",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Évora",
    provinceCode: "PT-07",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Faro",
    provinceCode: "PT-08",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Guarda",
    provinceCode: "PT-09",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Leiria",
    provinceCode: "PT-10",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Lisboa",
    provinceCode: "PT-11",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Madeira",
    provinceCode: "PT-30",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Portalegre",
    provinceCode: "PT-12",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Porto",
    provinceCode: "PT-13",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Santarém",
    provinceCode: "PT-14",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Setúbal",
    provinceCode: "PT-15",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Viana do Castelo",
    provinceCode: "PT-16",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Vila Real",
    provinceCode: "PT-17",
  },
  {
    country: "Portugal",
    countryCode: "PT",
    province: "Viseu",
    provinceCode: "PT-18",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Alba",
    provinceCode: "AB",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Arad",
    provinceCode: "AR",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Argeș",
    provinceCode: "AG",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Bacău",
    provinceCode: "BC",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Bihor",
    provinceCode: "BH",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Bistrița-Năsăud",
    provinceCode: "BN",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Botoșani",
    provinceCode: "BT",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Brașov",
    provinceCode: "BV",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Brăila",
    provinceCode: "BR",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "București",
    provinceCode: "B",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Buzău",
    provinceCode: "BZ",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Călărași",
    provinceCode: "CL",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Caraș-Severin",
    provinceCode: "CS",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Cluj",
    provinceCode: "CJ",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Constanța",
    provinceCode: "CT",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Covasna",
    provinceCode: "CV",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Dâmbovița",
    provinceCode: "DB",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Dolj",
    provinceCode: "DJ",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Galați",
    provinceCode: "GL",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Giurgiu",
    provinceCode: "GR",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Gorj",
    provinceCode: "GJ",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Harghita",
    provinceCode: "HR",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Hunedoara",
    provinceCode: "HD",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Iași",
    provinceCode: "IS",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Ialomița",
    provinceCode: "IL",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Ilfov",
    provinceCode: "IF",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Maramureș",
    provinceCode: "MM",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Mehedinți",
    provinceCode: "MH",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Mureș",
    provinceCode: "MS",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Neamț",
    provinceCode: "NT",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Olt",
    provinceCode: "OT",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Prahova",
    provinceCode: "PH",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Sălaj",
    provinceCode: "SJ",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Satu Mare",
    provinceCode: "SM",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Sibiu",
    provinceCode: "SB",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Suceava",
    provinceCode: "SV",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Teleorman",
    provinceCode: "TR",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Timiș",
    provinceCode: "TM",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Tulcea",
    provinceCode: "TL",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Vâlcea",
    provinceCode: "VL",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Vaslui",
    provinceCode: "VS",
  },
  {
    country: "Romania",
    countryCode: "RO",
    province: "Vrancea",
    provinceCode: "VN",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Adygeya",
    provinceCode: "AD",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Altai Republic",
    provinceCode: "AL",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Altai Krai",
    provinceCode: "ALT",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Amur Oblast",
    provinceCode: "AMU",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Arkhangelsk Oblast",
    provinceCode: "ARK",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Astrakhan Oblast",
    provinceCode: "AST",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Bashkortostan",
    provinceCode: "BA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Belgorod Oblast",
    provinceCode: "BEL",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Bryansk Oblast",
    provinceCode: "BRY",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Buryatia",
    provinceCode: "BU",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Chechen Republic",
    provinceCode: "CE",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Chelyabinsk Oblast",
    provinceCode: "CHE",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Chukotka Autonomous Okrug",
    provinceCode: "CHU",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Chuvash Republic",
    provinceCode: "CU",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Dagestan",
    provinceCode: "DA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Ingushetia",
    provinceCode: "IN",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Irkutsk Oblast",
    provinceCode: "IRK",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Ivanovo Oblast",
    provinceCode: "IVA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Jewish Autonomous Oblast",
    provinceCode: "YEV",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kabardino-Balkarian Republic",
    provinceCode: "KB",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kaliningrad Oblast",
    provinceCode: "KGD",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Kalmykia",
    provinceCode: "KL",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kaluga Oblast",
    provinceCode: "KLU",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kamchatka Krai",
    provinceCode: "KAM",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Karachay–Cherkess Republic",
    provinceCode: "KC",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Karelia",
    provinceCode: "KR",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kemerovo Oblast",
    provinceCode: "KEM",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Khabarovsk Krai",
    provinceCode: "KHA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Khakassia",
    provinceCode: "KK",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Khanty-Mansi Autonomous Okrug",
    provinceCode: "KHM",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kirov Oblast",
    provinceCode: "KIR",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Komi Republic",
    provinceCode: "KO",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kostroma Oblast",
    provinceCode: "KOS",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Krasnodar Krai",
    provinceCode: "KDA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Krasnoyarsk Krai",
    provinceCode: "KYA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kurgan Oblast",
    provinceCode: "KGN",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Kursk Oblast",
    provinceCode: "KRS",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Leningrad Oblast",
    provinceCode: "LEN",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Lipetsk Oblast",
    provinceCode: "LIP",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Magadan Oblast",
    provinceCode: "MAG",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Mari El Republic",
    provinceCode: "ME",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Mordovia",
    provinceCode: "MO",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Moscow",
    provinceCode: "MOW",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Moscow Oblast",
    provinceCode: "MOS",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Murmansk Oblast",
    provinceCode: "MUR",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Nizhny Novgorod Oblast",
    provinceCode: "NIZ",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Novgorod Oblast",
    provinceCode: "NGR",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Novosibirsk Oblast",
    provinceCode: "NVS",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Omsk Oblast",
    provinceCode: "OMS",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Orenburg Oblast",
    provinceCode: "ORE",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Oryol Oblast",
    provinceCode: "ORL",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Penza Oblast",
    provinceCode: "PNZ",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Perm Krai",
    provinceCode: "PER",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Primorsky Krai",
    provinceCode: "PRI",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Pskov Oblast",
    provinceCode: "PSK",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Rostov Oblast",
    provinceCode: "ROS",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Ryazan Oblast",
    provinceCode: "RYA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Saint Petersburg",
    provinceCode: "SPE",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Sakha Republic (Yakutia)",
    provinceCode: "SA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Sakhalin Oblast",
    provinceCode: "SAK",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Samara Oblast",
    provinceCode: "SAM",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Saratov Oblast",
    provinceCode: "SAR",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of North Ossetia–Alania",
    provinceCode: "SE",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Smolensk Oblast",
    provinceCode: "SMO",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Stavropol Krai",
    provinceCode: "STA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Sverdlovsk Oblast",
    provinceCode: "SVE",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Tambov Oblast",
    provinceCode: "TAM",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Republic of Tatarstan",
    provinceCode: "TA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Tomsk Oblast",
    provinceCode: "TOM",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Tula Oblast",
    provinceCode: "TUL",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Tver Oblast",
    provinceCode: "TVE",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Tyumen Oblast",
    provinceCode: "TYU",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Tyva Republic",
    provinceCode: "TY",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Udmurtia",
    provinceCode: "UD",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Ulyanovsk Oblast",
    provinceCode: "ULY",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Vladimir Oblast",
    provinceCode: "VLA",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Volgograd Oblast",
    provinceCode: "VGG",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Vologda Oblast",
    provinceCode: "VLG",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Voronezh Oblast",
    provinceCode: "VOR",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Yamalo-Nenets Autonomous Okrug",
    provinceCode: "YAN",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Yaroslavl Oblast",
    provinceCode: "YAR",
  },
  {
    country: "Russia",
    countryCode: "RU",
    province: "Zabaykalsky Krai",
    provinceCode: "ZAB",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "Eastern Cape",
    provinceCode: "EC",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "Free State",
    provinceCode: "FS",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "Gauteng",
    provinceCode: "GT",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "KwaZulu-Natal",
    provinceCode: "NL",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "Limpopo",
    provinceCode: "LP",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "Mpumalanga",
    provinceCode: "MP",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "North West",
    provinceCode: "NW",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "Northern Cape",
    provinceCode: "NC",
  },
  {
    country: "South Africa",
    countryCode: "ZA",
    province: "Western Cape",
    provinceCode: "WC",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Busan",
    provinceCode: "KR-26",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Chungbuk",
    provinceCode: "KR-43",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Chungnam",
    provinceCode: "KR-44",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Daegu",
    provinceCode: "KR-27",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Daejeon",
    provinceCode: "KR-30",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Gangwon",
    provinceCode: "KR-42",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Gwangju",
    provinceCode: "KR-29",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Gyeongbuk",
    provinceCode: "KR-47",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Gyeonggi",
    provinceCode: "KR-41",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Gyeongnam",
    provinceCode: "KR-48",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Incheon",
    provinceCode: "KR-28",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Jeju",
    provinceCode: "KR-49",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Jeonbuk",
    provinceCode: "KR-45",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Jeonnam",
    provinceCode: "KR-46",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Sejong",
    provinceCode: "KR-50",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Seoul",
    provinceCode: "KR-11",
  },
  {
    country: "South Korea",
    countryCode: "KR",
    province: "Ulsan",
    provinceCode: "KR-31",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "A Coruña",
    provinceCode: "C",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Álava",
    provinceCode: "VI",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Albacete",
    provinceCode: "AB",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Alicante",
    provinceCode: "A",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Almería",
    provinceCode: "AL",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Asturias",
    provinceCode: "O",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Ávila",
    provinceCode: "AV",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Badajoz",
    provinceCode: "BA",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Balears",
    provinceCode: "PM",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Barcelona",
    provinceCode: "B",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Burgos",
    provinceCode: "BU",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Cáceres",
    provinceCode: "CC",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Cádiz",
    provinceCode: "CA",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Cantabria",
    provinceCode: "S",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Castellón",
    provinceCode: "CS",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Ceuta",
    provinceCode: "CE",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Ciudad Real",
    provinceCode: "CR",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Córdoba",
    provinceCode: "CO",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Cuenca",
    provinceCode: "CU",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Girona",
    provinceCode: "GI",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Granada",
    provinceCode: "GR",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Guadalajara",
    provinceCode: "GU",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Guipúzcoa",
    provinceCode: "SS",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Huelva",
    provinceCode: "H",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Huesca",
    provinceCode: "HU",
  },
  { country: "Spain", countryCode: "ES", province: "Jaén", provinceCode: "J" },
  {
    country: "Spain",
    countryCode: "ES",
    province: "La Rioja",
    provinceCode: "LO",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Las Palmas",
    provinceCode: "GC",
  },
  { country: "Spain", countryCode: "ES", province: "León", provinceCode: "LE" },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Lleida",
    provinceCode: "L",
  },
  { country: "Spain", countryCode: "ES", province: "Lugo", provinceCode: "LU" },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Madrid",
    provinceCode: "M",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Málaga",
    provinceCode: "MA",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Melilla",
    provinceCode: "ML",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Murcia",
    provinceCode: "MU",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Navarra",
    provinceCode: "NA",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Ourense",
    provinceCode: "OR",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Palencia",
    provinceCode: "P",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Pontevedra",
    provinceCode: "PO",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Salamanca",
    provinceCode: "SA",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Santa Cruz de Tenerife",
    provinceCode: "TF",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Segovia",
    provinceCode: "SG",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Sevilla",
    provinceCode: "SE",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Soria",
    provinceCode: "SO",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Tarragona",
    provinceCode: "T",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Teruel",
    provinceCode: "TE",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Toledo",
    provinceCode: "TO",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Valencia",
    provinceCode: "V",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Valladolid",
    provinceCode: "VA",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Vizcaya",
    provinceCode: "BI",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Zamora",
    provinceCode: "ZA",
  },
  {
    country: "Spain",
    countryCode: "ES",
    province: "Zaragoza",
    provinceCode: "Z",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Amnat Charoen",
    provinceCode: "TH-37",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Ang Thong",
    provinceCode: "TH-15",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Bangkok",
    provinceCode: "TH-10",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Bueng Kan",
    provinceCode: "TH-38",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Buriram",
    provinceCode: "TH-31",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Chachoengsao",
    provinceCode: "TH-24",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Chai Nat",
    provinceCode: "TH-18",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Chaiyaphum",
    provinceCode: "TH-36",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Chanthaburi",
    provinceCode: "TH-22",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Chiang Mai",
    provinceCode: "TH-50",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Chiang Rai",
    provinceCode: "TH-57",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Chon Buri",
    provinceCode: "TH-20",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Chumphon",
    provinceCode: "TH-86",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Kalasin",
    provinceCode: "TH-46",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Kamphaeng Phet",
    provinceCode: "TH-62",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Kanchanaburi",
    provinceCode: "TH-71",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Khon Kaen",
    provinceCode: "TH-40",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Krabi",
    provinceCode: "TH-81",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Lampang",
    provinceCode: "TH-52",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Lamphun",
    provinceCode: "TH-51",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Loei",
    provinceCode: "TH-42",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Lopburi",
    provinceCode: "TH-16",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Mae Hong Son",
    provinceCode: "TH-58",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Maha Sarakham",
    provinceCode: "TH-44",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Mukdahan",
    provinceCode: "TH-49",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nakhon Nayok",
    provinceCode: "TH-26",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nakhon Pathom",
    provinceCode: "TH-73",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nakhon Phanom",
    provinceCode: "TH-48",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nakhon Ratchasima",
    provinceCode: "TH-30",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nakhon Sawan",
    provinceCode: "TH-60",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nakhon Si Thammarat",
    provinceCode: "TH-80",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nan",
    provinceCode: "TH-55",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Narathiwat",
    provinceCode: "TH-96",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nong Bua Lam Phu",
    provinceCode: "TH-39",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nong Khai",
    provinceCode: "TH-43",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Nonthaburi",
    provinceCode: "TH-12",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Pathum Thani",
    provinceCode: "TH-13",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Pattani",
    provinceCode: "TH-94",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Pattaya",
    provinceCode: "TH-S",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phangnga",
    provinceCode: "TH-82",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phatthalung",
    provinceCode: "TH-93",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phayao",
    provinceCode: "TH-56",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phetchabun",
    provinceCode: "TH-67",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phetchaburi",
    provinceCode: "TH-76",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phichit",
    provinceCode: "TH-66",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phitsanulok",
    provinceCode: "TH-65",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phra Nakhon Si Ayutthaya",
    provinceCode: "TH-14",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phrae",
    provinceCode: "TH-54",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Phuket",
    provinceCode: "TH-83",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Prachin Buri",
    provinceCode: "TH-25",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Prachuap Khiri Khan",
    provinceCode: "TH-77",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Ranong",
    provinceCode: "TH-85",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Ratchaburi",
    provinceCode: "TH-70",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Rayong",
    provinceCode: "TH-21",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Roi Et",
    provinceCode: "TH-45",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Sa Kaeo",
    provinceCode: "TH-27",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Sakon Nakhon",
    provinceCode: "TH-47",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Samut Prakan",
    provinceCode: "TH-11",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Samut Sakhon",
    provinceCode: "TH-74",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Samut Songkhram",
    provinceCode: "TH-75",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Saraburi",
    provinceCode: "TH-19",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Satun",
    provinceCode: "TH-91",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Sing Buri",
    provinceCode: "TH-17",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Sisaket",
    provinceCode: "TH-33",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Songkhla",
    provinceCode: "TH-90",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Sukhothai",
    provinceCode: "TH-64",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Suphan Buri",
    provinceCode: "TH-72",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Surat Thani",
    provinceCode: "TH-84",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Surin",
    provinceCode: "TH-32",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Tak",
    provinceCode: "TH-63",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Trang",
    provinceCode: "TH-92",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Trat",
    provinceCode: "TH-23",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Ubon Ratchathani",
    provinceCode: "TH-34",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Udon Thani",
    provinceCode: "TH-41",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Uthai Thani",
    provinceCode: "TH-61",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Uttaradit",
    provinceCode: "TH-53",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Yala",
    provinceCode: "TH-95",
  },
  {
    country: "Thailand",
    countryCode: "TH",
    province: "Yasothon",
    provinceCode: "TH-35",
  },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Abu Dhabi",
    provinceCode: "AZ",
  },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Ajman",
    provinceCode: "AJ",
  },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Dubai",
    provinceCode: "DU",
  },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Fujairah",
    provinceCode: "FU",
  },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Ras al-Khaimah",
    provinceCode: "RK",
  },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Sharjah",
    provinceCode: "SH",
  },
  {
    country: "United Arab Emirates",
    countryCode: "AE",
    province: "Umm al-Quwain",
    provinceCode: "UQ",
  },
  {
    country: "United Kingdom",
    countryCode: "GB",
    province: "British Forces",
    provinceCode: "BFP",
  },
  {
    country: "United Kingdom",
    countryCode: "GB",
    province: "England",
    provinceCode: "ENG",
  },
  {
    country: "United Kingdom",
    countryCode: "GB",
    province: "Northern Ireland",
    provinceCode: "NIR",
  },
  {
    country: "United Kingdom",
    countryCode: "GB",
    province: "Scotland",
    provinceCode: "SCT",
  },
  {
    country: "United Kingdom",
    countryCode: "GB",
    province: "Wales",
    provinceCode: "WLS",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Alabama",
    provinceCode: "AL",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Alaska",
    provinceCode: "AK",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "American Samoa",
    provinceCode: "AS",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Arizona",
    provinceCode: "AZ",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Arkansas",
    provinceCode: "AR",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "California",
    provinceCode: "CA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Colorado",
    provinceCode: "CO",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Connecticut",
    provinceCode: "CT",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Delaware",
    provinceCode: "DE",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "District of Columbia",
    provinceCode: "DC",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Federated States of Micronesia",
    provinceCode: "FM",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Florida",
    provinceCode: "FL",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Georgia",
    provinceCode: "GA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Guam",
    provinceCode: "GU",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Hawaii",
    provinceCode: "HI",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Idaho",
    provinceCode: "ID",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Illinois",
    provinceCode: "IL",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Indiana",
    provinceCode: "IN",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Iowa",
    provinceCode: "IA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Kansas",
    provinceCode: "KS",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Kentucky",
    provinceCode: "KY",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Louisiana",
    provinceCode: "LA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Maine",
    provinceCode: "ME",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Marshall Islands",
    provinceCode: "MH",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Maryland",
    provinceCode: "MD",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Massachusetts",
    provinceCode: "MA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Michigan",
    provinceCode: "MI",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Minnesota",
    provinceCode: "MN",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Mississippi",
    provinceCode: "MS",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Missouri",
    provinceCode: "MO",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Montana",
    provinceCode: "MT",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Nebraska",
    provinceCode: "NE",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Nevada",
    provinceCode: "NV",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "New Hampshire",
    provinceCode: "NH",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "New Jersey",
    provinceCode: "NJ",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "New Mexico",
    provinceCode: "NM",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "New York",
    provinceCode: "NY",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "North Carolina",
    provinceCode: "NC",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "North Dakota",
    provinceCode: "ND",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Northern Mariana Islands",
    provinceCode: "MP",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Ohio",
    provinceCode: "OH",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Oklahoma",
    provinceCode: "OK",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Oregon",
    provinceCode: "OR",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Palau",
    provinceCode: "PW",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Pennsylvania",
    provinceCode: "PA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Puerto Rico",
    provinceCode: "PR",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Rhode Island",
    provinceCode: "RI",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "South Carolina",
    provinceCode: "SC",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "South Dakota",
    provinceCode: "SD",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Tennessee",
    provinceCode: "TN",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Texas",
    provinceCode: "TX",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Utah",
    provinceCode: "UT",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Vermont",
    provinceCode: "VT",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Virginia",
    provinceCode: "VA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Washington",
    provinceCode: "WA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "West Virginia",
    provinceCode: "WV",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Wisconsin",
    provinceCode: "WI",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Wyoming",
    provinceCode: "WY",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Virgin Islands",
    provinceCode: "VI",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Armed Forces Americas",
    provinceCode: "AA",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Armed Forces Europe",
    provinceCode: "AE",
  },
  {
    country: "United States",
    countryCode: "US",
    province: "Armed Forces Pacific",
    provinceCode: "AP",
  },
];

// Built on first use. A top-level Set would make every bundle that imports
// this module carry the whole province table, used or not.
let countriesWithProvinces: ReadonlySet<string> | undefined;

/** Whether Shopify collects a province for addresses in this country. */
export function countryHasProvinces(countryCode: string | undefined): boolean {
  countriesWithProvinces ??= new Set(
    shopifyProvinces.map((province) => province.countryCode),
  );
  return !!countryCode && countriesWithProvinces.has(countryCode);
}
